<template>
    <div>
        <div class="modal fade" id="subscription-create-modal" tabindex="-1" aria-labelledby="subscription-create-modal" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ $t('create_new_subscription') }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>
                    <div class="modal-body" style="text-align:left">
                        <form @submit.prevent="save" class="subscription-form">
                            <div class="row">
                                <div class="col-sm-6 mb-3">
                                    <label class="form-label">{{ $t('client') }}</label>
                                    <auto-complete  
                                        @emitAutoCompleteClient="emitAutoCompleteClient" 
                                        v-if="createClientState === false" 
                                        :defaultValue="subscription.client?.name"
                                        :dislaySuggestions="true"
                                        entity="clients"
                                        emitEvent="emitAutoCompleteClient"
                                    ></auto-complete>
                                    <div class="related-create-link" style="padding: 5px;font-weight: bold;font-size: 12px;" @click="openCreateClientModal">{{ $t('create_new_client') }}</div>
                                </div>
                                <div class="col-sm-6 mb-3">
                                    <label class="form-label">{{ $t('plan') }}</label>
                                    <select v-model="subscription.plan_id" class="form-control" required>
                                        <option value="">{{ $t('select_plan') }}</option>
                                        <option v-for="plan in plans" :key="plan.id" :value="plan.id">
                                            {{ plan.name }} - {{ plan.price }} {{business.currency}}
                                        </option>
                                    </select>
                                </div>
                                <div class="col-sm-6 mb-3">
                                    <label class="form-label">{{ $t('start_date') }}</label>
                                    <input type="date" v-model="subscription.start_date" class="form-control" required />
                                </div>
                                <div class="col-sm-12 mb-3">
                                    <collapsible-notes-field v-model="subscription.notes" id="subscription-notes" rows="1" />
                                </div>

                                <!-- Payment Section -->
                                <div class="col-12 mt-3">
                                    <hr>
                                    <h6 class="mb-3">{{ $t('payment_information') }}</h6>
                                </div>
                                
                                <div class="col-sm-6 mb-3">
                                    <label class="form-label">{{ $t('payment_status') }}</label>
                                    <select v-model="subscription.payment_status" class="form-control">
                                        <option value="pending">{{ $t('pending') }}</option>
                                        <option value="paid">{{ $t('paid') }}</option>
                                        <option value="partial">{{ $t('partial') }}</option>
                                        <option value="failed">{{ $t('failed') }}</option>
                                    </select>
                                </div>

                                <div class="col-sm-6 mb-3">
                                    <label class="form-label">{{ $t('payment_method') }}</label>
                                    <select v-if="!loadingPaymentMethods" v-model="subscription.payment_method" class="form-control" :key="activePaymentMethods.length">
                                        <option v-for="method in activePaymentMethods" :key="method.id || method.name" :value="method.slug">
                                            {{ method.name }}
                                        </option>
                                    </select>
                                    <div v-else class="form-control">
                                        <small class="text-muted">{{ $t('loading') }}...</small>
                                    </div>
                                </div>

                                <div class="col-sm-6 mb-3" v-if="subscription.payment_method == 'cheque'">
                                    <label class="form-label">{{ $t('transaction_id') }}</label>
                                    <input 
                                        type="text" 
                                        v-model="subscription.transaction_id" 
                                        class="form-control" 
                                        :placeholder="$t('transaction_reference')" 
                                    />
                                </div>

                                <div class="col-sm-6 mb-3">
                                    <label class="form-label">{{ $t('amount_paid') }}</label>
                                    <input 
                                        type="number" 
                                        step="0.01" 
                                        v-model="amount_paid" 
                                        class="form-control" 
                                        :placeholder="selectedPlanPrice"
                                        :min="0" 
                                    />
                                    <small class="text-muted" v-if="selectedPlan">
                                        {{ $t('plan_price') }}: {{ selectedPlan.price }} {{ business.currency }}
                                    </small>
                                </div>

                                <div class="col-sm-6 mb-3">
                                    <label class="form-label">{{ $t('balance_due') }}</label>
                                    <div class="p-1" style="font-size:18px;font-weight:bold">{{ formatMoney(balanceDue) }}</div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('close') }}</button>
                        <button type="button" class="btn btn-primary" @click="save" :disabled="loading || !isFormValid">{{ $t('create') }}</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- client modal -->
        <add-client 
            v-if="createClientState"  
            :state="createClientState"  
            @createClient="createClient"/>
        <!-- client modal -->
    </div>
</template>

<script>
import helper from '../helper';
import AutoComplete from './autocomplete/AutoComplete';
import AddClient from './AddClient.vue';
import { getDefaultPaymentMethods } from '../constants/paymentMethods';

export default {
    name: 'AddSubscription',
    components: {
        AutoComplete,
        AddClient
    },
    props: ['state', 'plans'],
    data() {
        return {
            subscription: {
                client_id: '',
                plan_id: '',
                start_date: new Date().toISOString().substr(0, 10),
                notes: '',
                payment_status: 'pending',
                payment_method: '',
                transaction_id: ''
            },
            amount_paid: 0,
            createModal: null,
            loading: false,
            createClientState: false,
            paymentMethods: [],
            loadingPaymentMethods: false
        };
    },
    computed: {
        business() { return this.$store.getters.BUSINESS },
        isFormValid() {
            return this.subscription.client_id && this.subscription.plan_id;
        },
        selectedPlan() {
            if (!this.subscription.plan_id || !this.plans) return null;
            return this.plans.find(p => p.id == this.subscription.plan_id);
        },
        selectedPlanPrice() {
            return this.selectedPlan ? `${this.selectedPlan.price} ${this.business.currency}` : '';
        },
        balanceDue() {
            if (!this.selectedPlan) return 0;
            return this.selectedPlan.price - this.amount_paid;
        },
        activePaymentMethods(){
            // Filter only active payment methods or return the standard defaults
            const activeMethods = this.paymentMethods.filter(method => method.is_active)
            
            // Keep payment options available if the API cannot provide them
            if (activeMethods.length === 0) {
                return getDefaultPaymentMethods()
            }
            
            return activeMethods
        }
    },
    async mounted() {
        // eslint-disable-next-line no-undef
        this.createModal = new bootstrap.Modal(document.getElementById('subscription-create-modal'));
        
        // Fetch payment methods before showing modal
        await this.fetchPaymentMethods(true);
        
        if (this.state === true) {
            this.createModal.show();
        }

        document.getElementById('subscription-create-modal').addEventListener('hidden.bs.modal', () => {
            this.$emit('createSubscription', { state: false });
        });
    },
    methods: {
        async fetchPaymentMethods(forceDefault = false) {
            this.loadingPaymentMethods = true
            try {
                const response = await helper.customGet(`${process.env.VUE_APP_API}/payment-methods?per_page=100`)
                this.paymentMethods = response.data.data || []
                
                // If no payment methods exist, use the standard defaults
                if (this.paymentMethods.length === 0) {
                    this.paymentMethods = getDefaultPaymentMethods()
                }
                
                // Wait for loading to complete before setting default
                this.loadingPaymentMethods = false
                
                // Set the default payment method if not already defined or if forced
                if (forceDefault || !this.subscription.payment_method || this.subscription.payment_method === '') {
                    await this.$nextTick()
                    
                    const defaultMethod = this.paymentMethods.find(method => method.is_default === true)
                    let defaultSlug = 'cash'
                    
                    if (defaultMethod) {
                        defaultSlug = defaultMethod.slug
                    } else {
                        const firstActiveMethod = this.paymentMethods.find(method => method.is_active === true)
                        defaultSlug = firstActiveMethod ? firstActiveMethod.slug : 'cash'
                    }
                    
                    this.subscription.payment_method = defaultSlug
                    await this.$nextTick()
                }
            } catch (error) {
                this.paymentMethods = getDefaultPaymentMethods()
                this.loadingPaymentMethods = false
                if (forceDefault || !this.subscription.payment_method || this.subscription.payment_method === '') {
                    await this.$nextTick()
                    this.subscription.payment_method = 'cash'
                }
            }
        },
        async save() {
            try {
                this.loading = true;
                // Send payment info separately - backend will create transaction
                const payload = {
                    ...this.subscription,
                    amount_paid: this.amount_paid,
                    payment_status: this.subscription.payment_status,
                    payment_method: this.subscription.payment_method,
                    transaction_id: this.subscription.transaction_id
                };
                const response = await helper.customPost(`${process.env.VUE_APP_API}/subscriptions`, payload, { showToast: true });
                this.loading = false;
                this.clearForm();
                this.$emit('createSubscription', { subscription: response.data, state: false });
                this.createModal.hide();
            } catch (error) {
                this.loading = false;
            }
        },
        clearForm() {
            this.subscription = {
                client_id: '',
                plan_id: '',
                start_date: new Date().toISOString().substr(0, 10),
                notes: '',
                payment_status: '',
                payment_method: '',
                transaction_id: ''
            };
            this.amount_paid = 0;
        },
        emitAutoCompleteClient(data) {
            this.subscription.client_id = data.result.id;
            this.subscription.client = data.result;
        },
        openCreateClientModal() {
            this.createClientState = !this.createClientState;
        },
        createClient(data) {
            if (data.client) {
                this.subscription.client_id = data.client.id;
                this.subscription.client = data.client;
            }
            if (data && data.state != undefined) {
                this.createClientState = data.state;
            }
        },
        formatMoney(number) {
            return helper.formatMoney(number);
        }
    },
    watch: {
        'amount_paid'(newAmount) {
            if (!this.selectedPlan) return;
            const total = this.selectedPlan.price;
            
            if (newAmount >= total) {
                this.subscription.payment_status = 'paid';
            } else if (newAmount > 0 && newAmount < total) {
                this.subscription.payment_status = 'partial';
            } else if (newAmount == 0) {
                this.subscription.payment_status = 'pending';
            }
        },
        'subscription.payment_status'(newVal) {
            if (newVal == 'paid' && this.selectedPlan) {
                this.amount_paid = this.selectedPlan.price;
            }
        }
    }
};
</script>
